<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useElementSize } from '@vueuse/core';
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
//NOTE: required to make virtual-scroller work
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

import type { HotelSelectionCardData } from '../models';
import { useHotelSelectionListStore } from '../stores/hotelSelection/hotelSelectionListStore';

import HotelSelectionCard from './HotelSelectionCard.vue';

interface HotelSelectionDynamicScrollerProps {
  data: HotelSelectionCardData[];
  isMobile?: boolean;
}

const props = withDefaults(defineProps<HotelSelectionDynamicScrollerProps>(), {
  isMobile: false,
});

interface HotelSelectionDynamicScrollerEmits {
  (eventName: 'hightlight-on-map', hotelCode: string | undefined): void;
  (eventName: 'initialized-hotel-selection-list'): void;
}

const emit = defineEmits<HotelSelectionDynamicScrollerEmits>();

const dynamicScrollerRef = ref(null);
const { width } = useElementSize(dynamicScrollerRef);

// NOTE: CARD_WIDTH_BREAKPOINT is the same as the value content width in scss HotelSelectionCardLayout.vue file, remember to keep it always equal
const CARD_WIDTH_BREAKPOINT = 556;

const WIDE_CARD_GAP = 16;
const WIDE_CARD_HEIGHT = 240;
const WIDE_CARD_HEIGHT_WITH_GAP = WIDE_CARD_HEIGHT + WIDE_CARD_GAP;
const WIDE_SCROLL_THRESHOLD = 290;

const TALL_CARD_GAP = 16;
const TALL_CARD_HEIGHT = 510;
const TALL_CARD_HEIGHT_WITH_GAP = TALL_CARD_HEIGHT + TALL_CARD_GAP;
const TALL_SCROLL_THRESHOLD = 570;

const hotelSelectionListStore = useHotelSelectionListStore();

const formattedData = computed(() =>
  hotelSelectionListStore.getFormattedData(props.data, 1)
);

const cardHeight = computed<number>(() =>
  width.value >= CARD_WIDTH_BREAKPOINT
    ? WIDE_CARD_HEIGHT_WITH_GAP
    : TALL_CARD_HEIGHT_WITH_GAP
);
const scrollThreshold = computed<number>(() =>
  width.value >= CARD_WIDTH_BREAKPOINT
    ? WIDE_SCROLL_THRESHOLD
    : TALL_SCROLL_THRESHOLD
);

onMounted(() => emit('initialized-hotel-selection-list'));
</script>

<template>
  <DynamicScroller
    ref="dynamicScrollerRef"
    :items="formattedData"
    :min-item-size="cardHeight"
    :item-size="cardHeight"
    :buffer="scrollThreshold"
    :page-mode="isMobile"
    class="hotel-selection-dynamic-scroller"
  >
    <!-- NOTE: Please be extra carefull here! -->
    <!-- Because of vue-virtual-scroll variables here are NOT type safe, -->
    <!-- which means it is easy to brake this part of the code while changing object shapes -->
    <template #default="{ item, index, active }">
      <DynamicScrollerItem :item="item" :active="active" :data-index="index">
        <div class="grouped-cards">
          <HotelSelectionCard
            v-for="card of item.data"
            :key="card.hotelCode"
            class="card"
            :data="card"
            @hightlight-on-map="emit('hightlight-on-map', $event)"
          />
        </div>
      </DynamicScrollerItem>
    </template>
  </DynamicScroller>
</template>

<style scoped lang="scss">
.hotel-selection-dynamic-scroller {
  height: 100%;
  overflow-y: auto;
  padding: 0;

  @include for-less-or-equal-than-small-max {
    padding: 0 var(--spacing-16);
  }

  .grouped-cards {
    display: flex;
    gap: var(--spacing-16);

    .card {
      flex: 1;
    }
  }
}
</style>
